import {
  useNavigate,
  useLoaderData,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  ComponentProps,
} from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { MultiStepForm, StepDefinition } from "@/components/multi-step-form";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { validateAuthentication } from "@/utils/auth-middleware";
import { handleUnauthorized } from "@/utils/auth-utils";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { OrderAlert } from "@/components/order-alert";

import {
  StepDadosPrincipaisEdit,
  dadosPrincipaisEditSchema,
  dadosPrincipaisEditSchemaWithRefinement,
} from "./step-dados-principais-edit";
import {
  StepMidiasEdit,
  midiasEditSchema,
  validateMidiasEditStep,
} from "./step-midias-edit";
import {
  StepCaracteristicasEdit,
  caracteristicasEditSchema,
  validateCaracteristicasEditStep,
} from "./step-caracteristicas-edit";
import { useToast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { editPrivateOfficeLoader } from "./loader";
import { AmenitiesData } from "@/components/amenities-form/amenities-form.types";
import { apiWobaInventory } from "@/services/api-woba-inventory";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/authentication";
import { OutlierConfirmationDialog } from "@/components/statistical-alerts/dialogs/OutlierConfirmationDialog";
import { StatisticalAnalysisResponse } from "@/services/statistical-analysis-service";
import {
  UploadStateProvider,
  useUploadState,
} from "@/contexts/upload-state-context";
import { useAuthGuard } from "@/hooks/use-auth-guard.ts";
import { PrivateOfficeConfirmationModal } from "@/components/private-office-confirmation-modal";
import { photosWithoutTagsMessage } from "@/pages/private-office/photo-tags-guard";
import {
  getUserType,
  isLifecycleOnlyChange,
  shouldShowModal,
  UserType,
} from "@/utils/user-type-checker";
import {
  PrivateOfficeLifecycleState,
  isTransientLifecycleState,
} from "@/@types/private-office.types";

type LoaderData = Awaited<ReturnType<typeof editPrivateOfficeLoader>>;

const privateOfficeEditSchema = z.object({
  ...dadosPrincipaisEditSchema.shape,
  ...midiasEditSchema.shape,
  ...caracteristicasEditSchema.shape,
});

export type PrivateOfficeEditData = z.infer<typeof privateOfficeEditSchema>;

const hasDataChanged = (
  currentData: PrivateOfficeEditData,
  originalData: PrivateOfficeEditData,
): boolean => {
  const fieldsToCompare: (keyof PrivateOfficeEditData)[] = [
    "name",
    "description",
    "notes",
    "capacity",
    "size",
    "monthlyPrice",
    "status",
    "timezone",
    "lifecycleState",
    "availabilityDate",
  ];

  for (const field of fieldsToCompare) {
    const current = String(currentData[field] || "").trim();
    const original = String(originalData[field] || "").trim();
    if (current !== original) {
      return true;
    }
  }

  const currentPhotos = (currentData.photos || [])
    .map((p: any) => p.url)
    .sort();
  const originalPhotos = (originalData.photos || [])
    .map((p: any) => p.url)
    .sort();
  if (JSON.stringify(currentPhotos) !== JSON.stringify(originalPhotos)) {
    return true;
  }

  const currentVideo = currentData.videoUrls?.[0] || "";
  const originalVideo = originalData.videoUrls?.[0] || "";
  if (currentVideo !== originalVideo) {
    return true;
  }

  const currentAmenities = (currentData.selectedAmenities || [])
    .map((a: any) => ({ id: a.amenityId, status: a.status }))
    .sort((a, b) => a.id?.localeCompare(b.id));
  const originalAmenities = (originalData.selectedAmenities || [])
    .map((a: any) => ({ id: a.amenityId, status: a.status }))
    .sort((a, b) => a.id?.localeCompare(b.id));
  if (JSON.stringify(currentAmenities) !== JSON.stringify(originalAmenities)) {
    return true;
  }

  if (
    JSON.stringify(currentData.operatingHours) !==
    JSON.stringify(originalData.operatingHours)
  ) {
    return true;
  }

  return false;
};

const transformFormDataToPayload = (
  data: PrivateOfficeEditData,
  amenitiesData: AmenitiesData | null,
  userEmail: string,
  lifecycleOnly = false,
) => {
  const transformedPhotos = (data.photos || []).map((photo: any) => ({
    url: photo.url || "",
    tags: photo.tags || [],
  }));

  const transformedVideo = data.videoUrls?.[0] ? [data.videoUrls[0]] : [];

  const transformedAmenities = amenitiesData
    ? amenitiesData.amenities.map((group) => ({
        name: group.name,
        amenities: group.amenities.map((amenity) => {
          const userAmenity = (data.selectedAmenities || []).find(
            (selected: any) => selected.amenityId === amenity.id,
          );

          const finalStatus = userAmenity ? userAmenity.status : amenity.status;

          return {
            id: amenity.id,
            slug: amenity.slug,
            status: finalStatus,
            name: amenity.name,
            description: amenity.description,
            attributes:
              finalStatus === "included" || finalStatus === "not-included"
                ? []
                : amenity.attributes.map((attr) => ({
                    name: attr.name,
                    content:
                      userAmenity?.attributes?.[attr.name] || attr.content,
                    contentType: attr.contentType,
                  })),
          };
        }),
      }))
    : [];

  const externalSpaceId = data.externalSpaceId || "0";

  const transformOperatingHours = (operatingHours: any) => {
    if (operatingHours) {
      const daysOrder = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const result: any = {};

      daysOrder.forEach((day) => {
        const dayHours = operatingHours[day];
        result[day] = dayHours || [];
      });

      return result;
    }

    return {
      monday: [{ open: "09:00", close: "18:00" }],
      tuesday: [{ open: "09:00", close: "18:00" }],
      wednesday: [{ open: "09:00", close: "18:00" }],
      thursday: [{ open: "09:00", close: "18:00" }],
      friday: [{ open: "09:00", close: "18:00" }],
      saturday: [{ open: "10:00", close: "14:00" }],
      sunday: [],
    };
  };

  const isUnavailable = data.status === "unavailable";
  const lifecycleStateValue = isUnavailable
    ? (data.lifecycleState ?? undefined)
    : null;
  const availabilityDateValue = isUnavailable
    ? (data.availabilityDate ?? undefined)
    : null;

  if (lifecycleOnly) {
    return {
      status: data.status || "available",
      lifecycleState: lifecycleStateValue,
      availabilityDate: availabilityDateValue,
      userEmail: userEmail || "",
    };
  }

  const payload = {
    externalSpaceId: parseInt(externalSpaceId) || 0,
    name: data.name || "",
    description: data.description || "",
    notes: data.notes || "",
    photos: transformedPhotos,
    videoUrls: transformedVideo,
    capacity: parseInt(data.capacity) || 0,
    sizeM2: parseInt(data.size) || 0,
    monthlyPrice:
      parseFloat(data.monthlyPrice.replace(/\./g, "").replace(",", ".")) || 0,
    status: data.status || "available",
    lifecycleState: lifecycleStateValue,
    availabilityDate: availabilityDateValue,
    userEmail: userEmail || "",
    amenities: transformedAmenities,
    operatingHours: transformOperatingHours(data.operatingHours),
    timezone: data.timezone || "America/Sao_Paulo",
  };

  return payload;
};

function PrivateOfficeEditContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const loaderData = useLoaderData() as LoaderData;
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl");
  const { uploadState } = useUploadState();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState<
    "duplicated" | "wrong_register" | ""
  >("");

  const [showOutlierModal, setShowOutlierModal] = useState(false);
  const [statisticalData, setStatisticalData] = useState<any>(null);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<PrivateOfficeEditData | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showIrreversibleConfirmDialog, setShowIrreversibleConfirmDialog] =
    useState(false);
  const [pendingIrreversibleData, setPendingIrreversibleData] =
    useState<PrivateOfficeEditData | null>(null);
  const [userType, setUserType] = useState<UserType>("internal");
  const { userInfo } = useAuth();
  const params = useParams();
  const { userId, isExternalUser } = useAuthGuard();

  const amenitiesData = loaderData.amenitiesData;

  const shouldSkipFullValidation = useCallback(
    (formData: PrivateOfficeEditData) => {
      return formData.status === "unavailable";
    },
    [],
  );

  // Derive step loading state from upload context
  const stepLoadingState = useMemo(
    () => ({
      isLoading: uploadState.isUploading,
      loadingText: uploadState.uploadingText,
    }),
    [uploadState.isUploading, uploadState.uploadingText],
  );

  useEffect(() => {
    const cookies = document.cookie.split(";");
    const spaceIdCookie = cookies
      .find((cookie) => cookie.trim().startsWith("space_id="))
      ?.split("=")[1];

    const isExternalRoute = pathname.includes("/external/");
    const authResult = validateAuthentication(isExternalRoute);

    sendTrackDataServer({
      eventName: "PRIVATE_ROOM_INFO_STARTED",
      data: {
        user_id:
          userInfo?.user?.id ||
          authResult.payload?.id ||
          authResult.payload?.userId ||
          "unknown",
        email:
          userInfo?.profile?.email || authResult.payload?.email || "unknown",
        flow_type: "edit" as const,
        privateroom_id: params.privateOfficeId || "unknown",
        space_id: spaceIdCookie || "unknown",
      },
      url: pathname,
      productType: "PO",
    });
  }, [pathname, params.privateOfficeId, userInfo]);

  const validateLifecycleFields = (
    data: PrivateOfficeEditData,
  ): { valid: boolean; message?: string } => {
    if (data.status !== "unavailable") return { valid: true };

    if (!data.lifecycleState) {
      return {
        valid: false,
        message:
          "Para salvar, é necessário informar o motivo de indisponibilidade.",
      };
    }

    if (
      isTransientLifecycleState(
        data.lifecycleState as PrivateOfficeLifecycleState,
      ) &&
      !data.availabilityDate
    ) {
      return {
        valid: false,
        message:
          "A data prevista de retorno é obrigatória para o motivo selecionado.",
      };
    }

    if (data.availabilityDate) {
      const today = new Date().toISOString().split("T")[0];
      if (data.availabilityDate < today) {
        return {
          valid: false,
          message: "A data de retorno não pode ser anterior à data atual",
        };
      }
    }

    return { valid: true };
  };

  const handleUpdate = async (data: PrivateOfficeEditData) => {
    try {
      setIsSubmitting(true);

      const originalData =
        loaderData.privateOfficeData as PrivateOfficeEditData;

      // When skipping full validation (status → unavailable only),
      // validate lifecycle fields before proceeding
      if (shouldSkipFullValidation(data)) {
        const lifecycleValidation = validateLifecycleFields(data);
        if (!lifecycleValidation.valid) {
          toast({
            variant: "destructive",
            title: "Campos obrigatórios",
            description: lifecycleValidation.message,
          });
          setIsSubmitting(false);
          return;
        }
      }

      if (!hasDataChanged(data, originalData)) {
        toast({
          title: "Nenhuma alteração detectada",
          description:
            "Não há alterações para salvar. Modifique os dados antes de tentar salvar novamente.",
          variant: "default",
        });
        setIsSubmitting(false);
        return;
      }

      if (
        data.lifecycleState ===
          PrivateOfficeLifecycleState.ROOM_NO_LONGER_EXISTS &&
        originalData.lifecycleState !==
          PrivateOfficeLifecycleState.ROOM_NO_LONGER_EXISTS
      ) {
        setPendingIrreversibleData(data);
        setShowIrreversibleConfirmDialog(true);
        setIsSubmitting(false);
        return;
      }

      if (statisticalData?.hasExtremeOutliers?.hasAnyExtreme) {
        setPendingSubmissionData(data);
        setShowOutlierModal(true);
        setIsSubmitting(false);
        return;
      }

      const lifecycleOnly = isLifecycleOnlyChange(data, originalData);
      await executeSubmission(data, lifecycleOnly);
    } catch (error) {
      handleSubmissionError(error, data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmIrreversible = async () => {
    setShowIrreversibleConfirmDialog(false);
    if (!pendingIrreversibleData) return;
    const data = pendingIrreversibleData;
    setPendingIrreversibleData(null);
    try {
      setIsSubmitting(true);
      if (statisticalData?.hasExtremeOutliers?.hasAnyExtreme) {
        setPendingSubmissionData(data);
        setShowOutlierModal(true);
        return;
      }
      const originalData =
        loaderData.privateOfficeData as PrivateOfficeEditData;
      const lifecycleOnly = isLifecycleOnlyChange(data, originalData);
      await executeSubmission(data, lifecycleOnly);
    } catch (error) {
      handleSubmissionError(error, data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const executeSubmission = async (
    data: PrivateOfficeEditData,
    lifecycleOnly = false,
  ) => {
    // A API rejeita fotos com tags vazias (400); bloquear aqui evita enviar
    // payload inválido. lifecycleOnly não mexe em mídias, então não valida.
    if (!lifecycleOnly) {
      const missingTagsMessage = photosWithoutTagsMessage(data.photos);
      if (missingTagsMessage) {
        toast({
          variant: "destructive",
          title: "Erro ao atualizar sala privativa",
          description: missingTagsMessage,
        });
        return;
      }
    }

    toast({
      title: "Atualizando sala privativa...",
      description: "Salvando alterações",
    });

    const apiPayload = transformFormDataToPayload(
      data,
      amenitiesData,
      loaderData.userInfo.email,
      lifecycleOnly,
    );

    const privateOfficeId = params.privateOfficeId;
    if (!privateOfficeId) {
      throw new Error(
        "API Woba Inventory - ID da sala privativa não encontrado",
      );
    }

    await apiWobaInventory.patch(
      `/private-offices/${privateOfficeId}/edit-request`,
      apiPayload,
    );

    const cookies = document.cookie.split(";");
    const spaceIdCookie = cookies
      .find((cookie) => cookie.trim().startsWith("space_id="))
      ?.split("=")[1];
    const isExternalRoute = pathname.includes("/external/");
    const authResult = validateAuthentication(isExternalRoute);

    sendTrackDataServer({
      eventName: "UPDATE_ROOM_SUCCESS",
      data: {
        privateroom_id: privateOfficeId,
        space_id: data.externalSpaceId || spaceIdCookie || "unknown",
        user_id:
          authResult.payload?.id || authResult.payload?.userId || "unknown",
        email: authResult.payload?.email || "unknown",
      },
      url: pathname,
      productType: "PO",
    });

    const validPhotos = (data.photos || []).filter(
      (photo: any) => photo.url,
    ).length;
    if (validPhotos > 0) {
      toast({
        title: "Fotos processadas com sucesso!",
        description: `${validPhotos} foto${validPhotos > 1 ? "s" : ""} incluída${validPhotos > 1 ? "s" : ""}`,
      });
    }

    const detectedUserType = getUserType(userId ?? undefined, isExternalUser);
    setUserType(detectedUserType);

    const originalData = loaderData.privateOfficeData as PrivateOfficeEditData;
    if (
      !shouldShowModal(detectedUserType) ||
      isLifecycleOnlyChange(data, originalData)
    ) {
      toast({
        title: "Alterações salvas com sucesso!",
        description: "As alterações foram salvas e aprovadas automaticamente.",
      });
    } else {
      setShowConfirmationDialog(true);
    }
  };

  const handleSubmissionError = (error: any, data: PrivateOfficeEditData) => {
    const cookies = document.cookie.split(";");
    const spaceIdCookie = cookies
      .find((cookie) => cookie.trim().startsWith("space_id="))
      ?.split("=")[1];
    const isExternalRoute = pathname.includes("/external/");
    const authResult = validateAuthentication(isExternalRoute);
    const privateOfficeId = params.privateOfficeId;

    sendTrackDataServer({
      eventName: "UPDATE_ROOM_ERROR",
      data: {
        user_id:
          authResult.payload?.id || authResult.payload?.userId || "unknown",
        email: authResult.payload?.email || "unknown",
        privateroom_id: privateOfficeId || "unknown",
        space_id: data.externalSpaceId || spaceIdCookie || "unknown",
        error_message:
          (error as any).response?.data?.message ||
          (error as any).message ||
          "Unknown error",
      },
      url: pathname,
      productType: "PO",
    });

    let errorMessage = "Erro interno do sistema";
    let errorTitle = "Erro ao atualizar sala privativa";

    if (error instanceof Error) {
      errorMessage = error.message;

      if (
        error.message.includes("Token") ||
        error.message.includes("autentica")
      ) {
        errorTitle = "Erro de Autenticação";
        errorMessage = "Sua sessão expirou. Faça login novamente.";

        setTimeout(() => {
          handleUnauthorized();
        }, 2000);
      }
    }

    if ((error as any).response) {
      const status = (error as any).response.status;
      const responseData = (error as any).response.data;

      if (status === 401) {
        errorTitle = "Não Autorizado";
        errorMessage =
          "Token não encontrado ou expirado. Faça login novamente.";

        setTimeout(() => {
          handleUnauthorized();
        }, 2000);
      } else if (
        status === 400 &&
        responseData &&
        Array.isArray(responseData)
      ) {
        errorTitle = "Erro de Validação";
        errorMessage = responseData.join(", ");
      } else if (responseData && responseData.message) {
        errorMessage = responseData.message;
      }
    }

    toast({
      variant: "destructive",
      title: errorTitle,
      description: errorMessage,
    });
  };

  const handleStatisticalDataReady = useCallback(
    (data: {
      statisticalData: StatisticalAnalysisResponse;
      hasExtremeOutliers: any;
    }) => {
      setStatisticalData(data);
    },
    [],
  );

  const handleConfirmSubmission = async () => {
    setShowOutlierModal(false);
    if (!pendingSubmissionData) return;
    const data = pendingSubmissionData;
    setPendingSubmissionData(null);
    try {
      setIsSubmitting(true);
      await executeSubmission(data);
    } catch (error) {
      handleSubmissionError(error, data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmission = () => {
    setShowOutlierModal(false);
    setPendingSubmissionData(null);
    setIsSubmitting(false);
  };

  const handleDelete = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      setIsDeleting(true);

      const privateOfficeId = params.privateOfficeId;
      if (!privateOfficeId) {
        throw new Error("ID da sala privativa não encontrado");
      }

      toast({
        title: "Deletando sala privativa...",
        description: "Aguarde, estamos removendo a sala do sistema",
      });

      const queryParams = deleteReason ? `?deleteReason=${deleteReason}` : "";
      await apiWobaInventory.delete(
        `/private-offices/${privateOfficeId}${queryParams}`,
      );

      setIsDeleteModalOpen(false);
      setDeleteReason("");

      toast({
        title: "Sala deletada com sucesso!",
        description: "A sala foi removida do sistema",
      });

      const isExternalRoute = pathname.includes("/external/");
      const redirectRoute = isExternalRoute
        ? DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_CREATE_EXTERNAL
        : DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN;
      navigate(redirectRoute);
    } catch (error) {
      let errorMessage = "Erro interno do sistema";
      let errorTitle = "Erro ao deletar sala privativa";

      if (error instanceof Error) {
        errorMessage = error.message;

        if (
          error.message.includes("Token") ||
          error.message.includes("autentica")
        ) {
          errorTitle = "Erro de Autenticação";
          errorMessage = "Sua sessão expirou. Faça login novamente.";

          setTimeout(() => {
            handleUnauthorized();
          }, 2000);
        }
      }

      if ((error as any).response) {
        const status = (error as any).response.status;
        const responseData = (error as any).response.data;

        if (status === 401) {
          errorTitle = "Não Autorizado";
          errorMessage =
            "Token não encontrado ou expirado. Faça login novamente.";

          setTimeout(() => {
            handleUnauthorized();
          }, 2000);
        } else if (status === 403) {
          errorTitle = "Acesso Negado";
          errorMessage = "Você não tem permissão para deletar esta sala.";
        } else if (status === 404) {
          errorTitle = "Sala Não Encontrada";
          errorMessage =
            "A sala que você está tentando deletar não foi encontrada.";
        } else if (status === 409) {
          errorTitle = "Deleção bloqueada";
          errorMessage =
            "Não é possível deletar esta sala pois há contratos ativos vinculados.";
        } else if (responseData && responseData.message) {
          errorMessage = responseData.message;
        }
      }

      toast({
        variant: "destructive",
        title: errorTitle,
        description: errorMessage,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [params.privateOfficeId, navigate, toast]);

  const handleGoBack = () => {
    const isExternalRoute = pathname.includes("/external/");
    const authResult = validateAuthentication(isExternalRoute);

    let redirectRoute: string;

    if (authResult.isExternal) {
      redirectRoute = DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_CREATE_EXTERNAL;
    } else {
      redirectRoute = DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN;
    }

    navigate(redirectRoute);
  };

  // Função para lidar com o fechamento do diálogo de confirmação
  const handleCloseConfirmationDialog = useCallback(() => {
    setShowConfirmationDialog(false);
    // Handle redirect with returnUrl support
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    const isExternalRoute = pathname.includes("/external/");
    const authResult = validateAuthentication(isExternalRoute);

    let redirectRoute: string;

    if (authResult.isExternal) {
      return;
    } else {
      redirectRoute = DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN;
    }

    navigate(redirectRoute);
  }, [returnUrl, pathname, navigate]);

  const StepDadosPrincipaisEditWithData = useMemo(() => {
    const Component = (
      props: ComponentProps<typeof StepDadosPrincipaisEdit>,
    ) => (
      <StepDadosPrincipaisEdit
        {...props}
        onStatisticalDataReady={handleStatisticalDataReady}
      />
    );
    Component.displayName = "StepDadosPrincipaisEditWithData";
    return Component;
  }, [handleStatisticalDataReady]);

  const StepCaracteristicasEditWithData = useMemo(() => {
    const Component = (
      props: ComponentProps<typeof StepCaracteristicasEdit>,
    ) => <StepCaracteristicasEdit {...props} amenitiesData={amenitiesData} />;
    Component.displayName = "StepCaracteristicasEditWithData";
    return Component;
  }, [amenitiesData]);

  const steps: StepDefinition[] = useMemo(
    () => [
      {
        id: "dados-principais",
        title: "Dados principais",
        component: StepDadosPrincipaisEditWithData,
        schema: dadosPrincipaisEditSchemaWithRefinement,
        description: "Informações básicas sobre a sala privativa",
      },
      {
        id: "midias",
        title: "Mídias da sala",
        component: StepMidiasEdit,
        schema: midiasEditSchema,
        description: "Adicione fotos da sala",
        onValidateStep: validateMidiasEditStep,
      },
      {
        id: "caracteristicas",
        title: "Características e Serviços",
        component: StepCaracteristicasEditWithData,
        schema: caracteristicasEditSchema,
        description: "Amenidades e serviços disponíveis",
        onValidateStep: validateCaracteristicasEditStep,
      },
    ],
    [StepDadosPrincipaisEditWithData, StepCaracteristicasEditWithData],
  );

  const CustomFooter = useMemo(() => {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-4 z-50">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          {loaderData.canDelete && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="border-red-300 text-red-700 hover:bg-red-50 px-6 py-2.5"
            >
              <Trash2 size={16} className="mr-2" />
              Deletar sala
            </Button>
          )}

          <Button
            type="submit"
            form="multi-step-form"
            disabled={isSubmitting || !loaderData.canUpdate}
            className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-yellow-900 font-semibold px-8 py-2.5 shadow-md"
          >
            <Save size={16} className="mr-2" />
            {isSubmitting ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      </div>
    );
  }, [loaderData.canDelete, loaderData.canUpdate, isSubmitting, handleDelete]);

  const isExternalRoute = pathname.includes("/external/");

  if (!loaderData.hasPermission || loaderData.errorType) {
    if (loaderData.errorType === "ROOM_DELETED") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={32} className="text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Sala deletada
            </h1>
            <p className="text-gray-600 mb-6">
              {loaderData.errorMessage ||
                "Esta sala privativa foi deletada e não pode mais ser editada."}
            </p>
            <Button variant="outline" onClick={handleGoBack}>
              Voltar à lista
            </Button>
          </div>
        </div>
      );
    }

    if (loaderData.errorType === "ROOM_NOT_FOUND") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <h1 className="text-2xl font-bold text-orange-600 mb-4">
              Sala não encontrada 🔍
            </h1>
            <p className="text-gray-600 mb-4">
              {loaderData.errorMessage ||
                "Esta sala privativa não foi encontrada."}
            </p>
            <Button onClick={handleGoBack}>Voltar à lista</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Acesso Negado 🚫
          </h1>
          <p className="text-gray-600 mb-4">
            {loaderData.errorMessage ||
              "Você não tem permissão para editar esta sala."}
          </p>
          <Button onClick={handleGoBack}>Voltar à lista</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FCFBF9" }}>
      {!isExternalRoute && (
        <header className="bg-white border-b-2">
          <nav className="flex items-center justify-between gap-2 p-4">
            <section className="flex items-center gap-2">
              <Button size="icon" variant="outline" onClick={handleGoBack}>
                <ArrowLeft size={20} />
              </Button>
              <h1 className="font-bold">Editar sala privativa</h1>
            </section>
          </nav>
        </header>
      )}

      <div className="container mx-auto py-8 px-4 pb-32">
        <MultiStepForm
          title="Editar sala privativa"
          description="Revise ou edite os dados da sala para garantir que ela esteja atualizada e disponível."
          headerContent={<OrderAlert variant="edit" />}
          steps={steps}
          initialData={loaderData.privateOfficeData}
          onSubmit={handleUpdate}
          allowClickNavigation={true}
          nextButtonText="Próximo"
          submitButtonText="Salvar alterações"
          showStepIndicator={true}
          stepLoadingState={stepLoadingState}
          className="mb-24"
          customFooter={CustomFooter}
          validateAllOnSubmit={true}
          shouldSkipFullValidation={shouldSkipFullValidation}
        />
      </div>

      <Dialog
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open);
          if (!open) setDeleteReason("");
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Deletar sala privativa</DialogTitle>
            <DialogDescription>
              Selecione o motivo da deleção. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-3">
            <label
              htmlFor="delete-reason"
              className="text-sm font-medium text-gray-700"
            >
              Motivo da deleção <span className="text-red-500">*</span>
            </label>
            <Select
              value={deleteReason}
              onValueChange={(value) =>
                setDeleteReason(value as "duplicated" | "wrong_register")
              }
              disabled={isDeleting}
            >
              <SelectTrigger id="delete-reason" className="w-full">
                <SelectValue placeholder="Selecione o motivo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="duplicated">Sala duplicada</SelectItem>
                <SelectItem value="wrong_register">
                  Sala cadastrada por engano
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteReason("");
              }}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting || !deleteReason}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Deletando...
                </>
              ) : (
                "Deletar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OutlierConfirmationDialog
        isOpen={showOutlierModal}
        onClose={handleCancelSubmission}
        onConfirm={handleConfirmSubmission}
        statisticalData={statisticalData?.statisticalData}
      />

      <PrivateOfficeConfirmationModal
        isOpen={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        userType={userType}
        actionType="edit"
      />

      <Dialog
        open={showIrreversibleConfirmDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowIrreversibleConfirmDialog(false);
            setPendingIrreversibleData(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ação irreversível</DialogTitle>
            <DialogDescription>
              Ao marcar esta sala como &quot;Sala não existe mais&quot;, a ação
              não poderá ser desfeita. A sala permanecerá no sistema para fins
              de histórico financeiro e auditoria, mas não voltará a ficar
              disponível.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowIrreversibleConfirmDialog(false);
                setPendingIrreversibleData(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmIrreversible}
              className="bg-red-600 hover:bg-red-700"
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
}

export default function PrivateOfficeEdit() {
  return (
    <UploadStateProvider>
      <PrivateOfficeEditContent />
    </UploadStateProvider>
  );
}
